import { useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    CircularProgress,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';

import { apiQuery } from '../../../state';
import {
    AnimatedTranslationIcon,
    getTranslationEstimatedTimeRemaining,
    useTranslationProgress,
} from '../../Translations';

/**
 * Blankslate when the computed content for a translation generated an empty result.
 * It means the original source content is not yet translated.
 *
 * This blankslate is for the space screen.
 */
export function ContentSourceTranslationSpaceBlankslate(props: {
    space: api.Space;
    source: api.ComputedContentSourceRevisionTranslation;
}) {
    const { space, source } = props;

    const { data: translation } = useSuspenseQuery(
        apiQuery(
            'getTranslation',
            {
                organizationId: space.organization,
                translationId: source.dependencies.translation.ref.translation,
            },
            {
                nullOnError: true,
            }
        )
    );

    if (translation) {
        return <BlankslateForTranslation translation={translation} />;
    }

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.AITranslate />
            </EmptyStateIcon>
            <Heading>Translation not found</Heading>
            <Description>
                The translation workflow can't be found. It might have been deleted.
            </Description>
        </EmptyState>
    );
}

function BlankslateForTranslation(props: { translation: api.Translation }) {
    const { translation } = props;

    const progress = useTranslationProgress(translation.id);

    return (
        <EmptyState>
            <EmptyStateIcon>
                <AnimatedTranslationIcon size={32} />
            </EmptyStateIcon>
            <Heading>Translation in progress</Heading>
            <Description>
                The content will display once it's done. It might take a few minutes, depending on
                the size of the content.
            </Description>
            <div className="mt-3">
                <Badge
                    kind="secondary"
                    icon={<CircularProgress value={progress?.progress ?? 0} className="size-5" />}
                    label={`${(progress?.progress ?? 0).toFixed(0)}%: Estimated time remaining: ${progress ? getTranslationEstimatedTimeRemaining(progress) : 'Unknown'}`}
                />
            </div>
        </EmptyState>
    );
}
